import { useEffect, useMemo, useRef, useState } from "react";
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";
import { FaCircleCheck, FaPrint, FaTruck } from "react-icons/fa6";

import banner1 from "../assets/banner1.jpg";
import banner2 from "../assets/banner2.jpg";
import banner3 from "../assets/banner3.jpg";
import banner4 from "../assets/banner4.jpg";
import banner5 from "../assets/banner5.jpg";

/* ------------------------------------------------------------------ */
/* Scroll-reveal hook for smooth entry animations                     */
/* ------------------------------------------------------------------ */
function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px", ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ as: Tag = "div", children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component                                                     */
/* ------------------------------------------------------------------ */
export default function View() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  const milestones = useMemo(
    () => [
      { year: "2017", label: "Established", text: "The business started with a logistics-first mindset." },
      { year: "2021", label: "Registered", text: "Registered as a business name in Tanzania." },
      { year: "2022", label: "Incorporated", text: "Formally incorporated as a limited company." },
      { year: "Now", label: "Multi-sector", text: "Serving freight and corporate supply needs in one place." },
    ],
    []
  );

  const pillars = useMemo(
    () => [
      {
        title: "Global Logistics & Freight Forwarding",
        icon: <FaTruck size={24} />,
        text: "We simplify cargo movement through air, ocean, road, rail, customs clearance, warehousing, project cargo, and cold chain support.",
      },
      {
        title: "Printing & Corporate Supplies",
        icon: <FaPrint size={24} />,
        text: "We support businesses, schools, NGOs, hospitals, and courts with printing, stationery, office machines, and technology supplies.",
      },
    ],
    []
  );

  const values = useMemo(
    () => [
      "Teamwork",
      "Integrity",
      "Professionalism",
      "Innovation",
      "Quality",
      "Customer-Centricity",
      "Sustainability",
    ],
    []
  );

  if (!ready) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  return (
    <section className="mod-about-page">
      <style>{`
        /* --- Base & Dark Theme --- */
        .mod-about-page {
          background-color: #0f172a; /* Deep Navy Slate */
          color: #f8fafc;
          font-family: system-ui, -apple-system, sans-serif;
          overflow-x: hidden;
        }

        /* --- Scroll Reveal Animations --- */
        .reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        .reveal.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* --- Hero Section --- */
        .mod-hero {
          position: relative;
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 6rem 1.5rem;
          overflow: hidden;
        }
        .mod-hero-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }
        .mod-hero-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transform: scale(1.05);
          animation: slowZoom 20s ease-out forwards;
        }
        .mod-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(15, 23, 42, 0.6) 0%, #0f172a 100%);
        }
        .mod-hero-content {
          position: relative;
          z-index: 1;
          max-width: 800px;
        }
        .mod-hero-title {
          font-size: clamp(2.5rem, 6vw, 4.5rem);
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 1rem;
          letter-spacing: -0.02em;
        }
        .mod-hero-title span {
          color: #3b82f6; /* Bright Blue */
        }
        .mod-hero-lead {
          font-size: clamp(1.1rem, 2.5vw, 1.25rem);
          color: #94a3b8;
          line-height: 1.6;
          font-weight: 400;
        }

        /* --- Common Section Styles --- */
        .mod-section {
          padding: 6rem 1.5rem;
          position: relative;
        }
        .mod-section-alt {
          background-color: #1e293b; /* Slightly lighter slate */
          border-top: 1px solid rgba(255,255,255,0.05);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .mod-kicker {
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #60a5fa;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: block;
        }
        .mod-subtitle {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 3rem;
          line-height: 1.2;
        }

        /* --- Cards & Glassmorphism --- */
        .mod-card {
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 2.5rem;
          backdrop-filter: blur(10px);
          transition: transform 0.3s ease, border-color 0.3s ease;
        }
        .mod-card:hover {
          transform: translateY(-5px);
          border-color: rgba(96, 165, 250, 0.3);
        }

        /* --- Grid Layouts --- */
        .mod-story-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          margin-bottom: 4rem;
        }
        @media (min-width: 992px) {
          .mod-story-grid { grid-template-columns: 1.2fr 1fr; align-items: center; }
        }
        .mod-story-img-wrapper {
          border-radius: 16px;
          overflow: hidden;
          position: relative;
        }
        .mod-story-img-wrapper::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 16px;
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.1);
        }
        .mod-story-img {
          width: 100%;
          height: auto;
          display: block;
        }

        /* --- Milestones --- */
        .mod-milestones {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
        }
        .mod-milestone {
          background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
          border: 1px solid rgba(255,255,255,0.05);
          border-left: 3px solid #3b82f6;
          padding: 1.5rem;
          border-radius: 8px;
        }
        .mod-milestone-year {
          font-size: 1.5rem;
          font-weight: 800;
          color: #60a5fa;
          margin-bottom: 0.5rem;
        }
        .mod-milestone h3 { font-size: 1.1rem; color: #fff; margin-bottom: 0.5rem; }
        .mod-milestone p { color: #94a3b8; font-size: 0.95rem; margin: 0; }

        /* --- Mission & Vision --- */
        .mod-mv-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }
        .mod-mv-card h3 { font-size: 1.5rem; color: #fff; margin-bottom: 1rem; }
        .mod-mv-card p { color: #94a3b8; line-height: 1.6; margin-bottom: 1.5rem; }
        .mod-badges { display: flex; gap: 0.5rem; flex-wrap: wrap; }
        .mod-badge {
          background: rgba(59, 130, 246, 0.1);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.2);
          padding: 0.35rem 0.8rem;
          border-radius: 50px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        /* --- Pillars --- */
        .mod-pillars-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }
        .mod-pillar-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          background: rgba(59, 130, 246, 0.1);
          color: #60a5fa;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        /* --- Values --- */
        .mod-values {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .mod-value {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: rgba(30, 41, 59, 0.6);
          border: 1px solid rgba(255,255,255,0.05);
          padding: 0.75rem 1.25rem;
          border-radius: 50px;
          color: #e2e8f0;
          font-weight: 500;
        }
        .mod-value svg { color: #3b82f6; }

        /* --- Image Strip --- */
        .mod-strip {
          display: flex;
          width: 100%;
          height: 250px;
        }
        .mod-strip img {
          flex: 1;
          object-fit: cover;
          filter: grayscale(100%) opacity(0.5);
          transition: all 0.5s ease;
          min-width: 0; /* Prevents overflow */
        }
        .mod-strip img:hover {
          filter: grayscale(0%) opacity(1);
          flex: 1.5;
        }

        @keyframes slowZoom {
          from { transform: scale(1); }
          to { transform: scale(1.1); }
        }
      `}</style>

      {/* ---------- HERO ---------- */}
      <header className="mod-hero">
        <div className="mod-hero-bg">
          <img src={banner1} alt="PACLESE CARGO operations" className="mod-hero-img" />
          <div className="mod-hero-overlay" />
        </div>

        <div className="container mod-hero-content">
          <Reveal>
            <h1 className="mod-hero-title">Who <span>We Are</span></h1>
          </Reveal>
          <Reveal delay={100}>
            <p className="mod-hero-lead">
              PACLESE CARGO Freight & Forwarding Company Limited is a dynamic
              multi-sector enterprise built to make logistics, procurement, and
              corporate support feel simple and dependable.
            </p>
          </Reveal>
        </div>
      </header>

      {/* ---------- STORY & MILESTONES ---------- */}
      <section className="mod-section">
        <div className="container">
          <div className="mod-story-grid">
            <Reveal className="mod-card">
              <span className="mod-kicker">Our Story</span>
              <h2 className="mod-subtitle" style={{ marginBottom: '1.5rem', fontSize: '2rem' }}>
                From a freight company to a broader corporate partner
              </h2>
              <p style={{ color: '#94a3b8', lineHeight: '1.7', fontSize: '1.05rem' }}>
                Established in 2017, registered in 2021, and incorporated in
                2022, PACLESE CARGO has grown into a practical partner for
                companies that need shipping, print, stationery, machines, and
                technology support without complexity.
              </p>
            </Reveal>

            <Reveal delay={100} className="mod-story-img-wrapper">
              <img src={banner2} alt="Cargo and logistics" className="mod-story-img" />
            </Reveal>
          </div>

          <div className="mod-milestones">
            {milestones.map((item, i) => (
              <Reveal key={item.year} delay={i * 100} className="mod-milestone">
                <div className="mod-milestone-year">{item.year}</div>
                <h3>{item.label}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- MISSION & VISION ---------- */}
      <section className="mod-section mod-section-alt">
        <div className="container">
          <Reveal>
            <span className="mod-kicker">Mission & Vision</span>
            <h2 className="mod-subtitle">What Guides Our Work</h2>
          </Reveal>

          <div className="mod-mv-grid">
            <Reveal delay={100} className="mod-card mod-mv-card">
              <span className="mod-kicker" style={{ color: '#fff', opacity: 0.6 }}>Mission</span>
              <h3>Logistics & Freight Forwarding</h3>
              <p>
                To deliver excellence through tailored logistics solutions,
                ensuring seamless operations, regional connectivity, and
                customer satisfaction.
              </p>
              <div className="mod-badges">
                <span className="mod-badge">Air & Ocean Cargo</span>
                <span className="mod-badge">Customs Clearance</span>
                <span className="mod-badge">Warehousing</span>
              </div>
            </Reveal>

            <Reveal delay={200} className="mod-card mod-mv-card">
              <span className="mod-kicker" style={{ color: '#fff', opacity: 0.6 }}>Vision</span>
              <h3>Printing & Corporate Supplies</h3>
              <p>
                To emerge as a trusted first-choice provider of printing and
                supply services, known for innovation, quality, and sustainability.
              </p>
              <div className="mod-badges">
                <span className="mod-badge">Printing</span>
                <span className="mod-badge">Stationery</span>
                <span className="mod-badge">Technology</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- CORE PILLARS ---------- */}
      <section className="mod-section">
        <div className="container">
          <Reveal>
            <span className="mod-kicker">Two Core Pillars</span>
            <h2 className="mod-subtitle">Built Around the Way You Work</h2>
          </Reveal>

          <div className="mod-pillars-grid">
            {pillars.map((pillar, i) => (
              <Reveal key={pillar.title} delay={i * 100} className="mod-card mod-mv-card">
                <div className="mod-pillar-icon">{pillar.icon}</div>
                <h3>{pillar.title}</h3>
                <p>{pillar.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- VALUES ---------- */}
      <section className="mod-section mod-section-alt">
        <div className="container">
          <Reveal>
            <span className="mod-kicker">Our Values</span>
            <h2 className="mod-subtitle">What Keeps Us Grounded</h2>
          </Reveal>

          <div className="mod-values">
            {values.map((value, i) => (
              <Reveal as="span" key={value} delay={i * 50} className="mod-value">
                <FaCircleCheck size={16} />
                {value}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- IMAGE STRIP ---------- */}
      <Reveal as="section" className="mod-strip">
        {[banner3, banner4, banner5, banner2].map((image, idx) => (
          <img key={idx} src={image} alt={`Operations detail ${idx + 1}`} loading="lazy" />
        ))}
      </Reveal>

      <Footer />
    </section>
  );
}